import { getJsonFileFromS3 } from './aws';

const SITE_SETTING_JSON_S3_KEY = `config/site_settings.json`;

/**
 * トップページの特集・コラボ企画の設定
 */
export type TopSpecialElement = {
  trackingId: string;
  imgSrc?: string; // publicフォルダ配下の画像。imgS3Keyよりも優先される
  imgS3Key?: string; // S3からAPI経由でダウンロードする画像
  linkUrl: string;
  description: string;
};

/**
 * 表示制御設定
 */
export type Visibility = {
  isVerifiedDoctorOnly?: boolean; // trueの場合、認証済み医師のみにバナーを表示する
};

/**
 * 特集ページ、スライド詳細バナー設定
 */
export type Special = {
  name: string;
  link: string;
  isExternalLink?: boolean;
  mainImage?: string; // バナー画像パス（mainImageS3Keyよりも優先される）
  mainImageS3Key?: string; // バナー画像S3キー
  detailBannerImage?: string; // スライド詳細ページのバナー画像 無い場合はmainImageを使用（mainImageS3Keyよりも優先される）
  detailBannerImageS3Key?: string; // スライド詳細ページのバナー画像S3キー
  mobileBannerImage?: string; // モバイル用バナー画像パス（mainImageS3Keyよりも優先される）
  mobileBannerImageS3Key?: string; // モバイル用バナー画像S3キー
  alt: string;
  tags: string[];
  andTags?: string[][]; // 複数のタグが含まれているスライドを指定する場合に使用、タグ一つでも可
  articleIds?: string[];
  excludeArticleIds?: string[]; // 表示対象から除外するarticleId（bannersでのみ有効）
  /**
   * スライド詳細の関連スライドを特集のtagsで取得する場合はtrue
   */
  relatedSlidesPreferSpecialTags?: boolean;
  visibility?: Visibility;
};

export type MobileSpecialBar = {
  key: string;
  image?: string; // バナー画像パス（imageS3Keyよりも優先される）
  imageS3Key?: string; // バナー画像S3キー
  alt: string;
  link: string;
  tags: string[];
  articleIds?: string[];
  excludeArticleIds?: string[]; // 表示対象から除外するarticleId（mobileSpecialBarsでのみ有効）
  departmentName?: string;
  // バナー画像のデフォルトサイズを1200x240とし、縦横比が違う場合はwidth, heightを指定する
  width?: number;
  height?: number;
  visibility?: Visibility;
};

export type BannerSettings = {
  bannerKeys?: string[];
  banners?: {
    [key in string[number]]: Special;
  };
  articleIdBannerKeysFormer?: string[];
  articleIdBannerFormer?: {
    [key in string[number]]: Special;
  };
  departmentBannerKeys?: string[];
  departmentBanners?: {
    [key in string[number]]: Special; // 注）表示制御対象外
  };
  mobileArticleIdBannerFormer?: MobileSpecialBar[];
  mobileSpecialBars?: MobileSpecialBar[];
  mobileDepartmentBars?: MobileSpecialBar[]; // 注）表示制御対象外
};

// ============================================================
// BannerSettingsEx: 新バナー設定スキーマ（bannerSettingsの後継）
// ============================================================

/** バナー画像セット（デバイス共通） */
export type BannerImages = {
  /** PCビュー用定義 */
  pc: {
    imgS3Key?: string; // バナー画像S3キー
  };
  /** モバイルビュー用定義 */
  mobile: {
    imgS3Key?: string; // バナー画像S3キー
  };
};

/** バナークリエイティブ定義 */
export type BannerCreative = {
  name: string;
  alt: string;
  linkUrl: string;
  isExternalLink?: boolean;
  images: BannerImages;
};

/** ターゲティング条件 */
export type BannerTargeting =
  | { type: 'articleId'; values: string[]; excludeArticleIds?: string[] }
  | { type: 'tag'; values: string[]; excludeArticleIds?: string[] }
  | { type: 'andTags'; values: string[][]; excludeArticleIds?: string[] }
  | { type: 'department'; values: string[]; excludeArticleIds?: string[] }
  | { type: 'all'; excludeArticleIds?: string[] };

/** ローテーション内の1エントリ */
export type InventoryEntry = {
  creativeId: string;
  weight: number;
};

/** バナールール（ターゲティング＋ローテーション） */
export type BannerRule = {
  id: string;
  priority: number; // 値が大きいほど優先
  targeting: BannerTargeting;
  visibility?: {
    isVerifiedDoctorOnly?: boolean;
  };
  inventories: InventoryEntry[];
};

/** 表示オプション（全バナー共通） */
export type DisplayOptions = {
  rotationIntervalSec: number; // 0以下の場合はローテーションしない
};

/** 新しいバナー設定 */
export type BannerSettingsEx = {
  displayOptions: DisplayOptions;
  creatives: Record<string, BannerCreative>;
  rules: BannerRule[];
  fillers: InventoryEntry[];
};

// ============================================================

/**
 * サイト設定ファイル（settings.json）用JSONデータの型
 */
export type SiteSettings = {
  topSpecialElements?: TopSpecialElement[];
  bannerSettings?: BannerSettings;
  /**
   * 新バナー設定（bannerSettingsの後継）。
   * bannerSettingsExが存在する場合は、スライド詳細でこちらを優先して使用する。
   */
  bannerSettingsEx?: BannerSettingsEx;
  /**
   * 外部編集ツール等で追加されるメタデータ。Webアプリ側では基本的に利用しない。
   */
  metadata?: any;
};

/**
 * サイト設定を取得する
 *
 * @returns サイト設定。S3に設定ファイルが存在しない場合、または読み込みに失敗した場合は null を返す。
 * @throws JSONのパースに失敗した場合など、取得に失敗した場合は例外を投げる。
 */
export async function getSiteSettings(): Promise<SiteSettings | null> {
  let settings = null;
  try {
    settings = await getJsonFileFromS3(SITE_SETTING_JSON_S3_KEY);
  } catch (e) {
    console.error('サイト設定の取得に失敗しました。', e);
  }
  return settings as SiteSettings;
}

/**
 * S3からの画像ダウンロードURLを生成する
 *
 * @param s3Key S3キー
 * @returns 画像ダウンロードURL
 */
export const generateS3DownloadUrl = (s3Key?: string | null): string => {
  return s3Key ? `/api/common/image?key=${encodeURIComponent(s3Key)}` : '';
};
